/**
 * Credit Validation Alert Component
 * Shows credit validation status and warnings before operations
 */

import React, { useEffect, useState } from 'react';
import { creditService } from '../services/credit.service';

interface CreditValidationAlertProps {
  operationType: string;
  className?: string;
  onValidationChange?: (isValid: boolean, details?: any) => void;
}

const CreditValidationAlert: React.FC<CreditValidationAlertProps> = ({
  operationType,
  className = '',
  onValidationChange,
}) => {
  const [validation, setValidation] = useState<{
    hasCredits: boolean;
    required: number;
    available: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    validateCredits();
  }, [operationType]);

  useEffect(() => {
    if (onValidationChange && validation) {
      onValidationChange(validation.hasCredits, validation);
    }
  }, [validation, onValidationChange]);

  const validateCredits = async () => {
    try {
      setLoading(true);
      const result = await creditService.validateCredits(operationType);

      if (result.success) {
        setValidation({
          hasCredits: result.hasCredits!,
          required: result.required!,
          available: result.available!,
        });
        setError(null);
      } else {
        setError(result.error || 'Failed to validate credits');
      }
    } catch (err) {
      setError('Failed to validate credits');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-md p-4 ${className}`}>
        <div className="animate-pulse flex items-center">
          <div className="w-4 h-4 bg-gray-300 rounded-full mr-3"></div>
          <div className="h-4 bg-gray-300 rounded w-48"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-md p-4 ${className}`}>
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-red-800">Credit validation failed</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={validateCredits}
          className="mt-2 text-xs text-red-700 hover:text-red-900 underline"
        >
          Retry validation
        </button>
      </div>
    );
  }

  if (!validation) {
    return null;
  }

  if (validation.hasCredits) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-md p-4 ${className}`}>
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-green-800">
              Credits available for {creditService.getOperationTypeDisplay(operationType)}
            </p>
            <p className="text-xs text-green-600 mt-1">
              Cost: {creditService.formatAmount(validation.required)} | 
              Available: {creditService.formatAmount(validation.available)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Insufficient credits
  return (
    <div className={`bg-red-50 border border-red-200 rounded-md p-4 ${className}`}>
      <div className="flex items-start">
        <svg className="w-5 h-5 text-red-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800">
            Insufficient credits for {creditService.getOperationTypeDisplay(operationType)}
          </p>
          <div className="mt-2 text-xs text-red-600">
            <p>Required: {creditService.formatAmount(validation.required)}</p>
            <p>Available: {creditService.formatAmount(validation.available)}</p>
            <p className="mt-1 font-medium">
              Shortfall: {creditService.formatAmount(validation.required - validation.available)}
            </p>
          </div>
          <div className="mt-3">
            <p className="text-xs text-red-700 font-medium">
              Please contact your administrator to load more credits.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditValidationAlert;